import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type {
    PlannableTriggerKind,
    VirtualAppointmentSnoozeTrigger
} from '../../services/virtualAppointments';
import { ModalShell } from '../shared/ModalShell';
import { VirtualAppointmentTriggerField } from './VirtualAppointmentTriggerField';
import {
    initialSnoozeDraft,
    validateSnoozeDraft,
    type SnoozeDraft,
    type SnoozeLimits
} from './virtualAppointmentSnooze';
import { todayUtcIso } from './virtualAppointmentWeeks';
import { parseWeeksInput } from './virtualAppointmentTriggerInput';
import { nogosUnavailableHint } from './virtualAppointmentNogos';
import { NO_TREATMENT_START_ON_RECORD } from './virtualAppointmentWeeksHint';
import { snoozeTriggerBaseline } from './virtualAppointmentTriggerBaseline';

const TITLE_ID = 'va-snooze-title';
const TRIGGER_INPUT_ID = 'va-snooze-trigger';

interface VirtualAppointmentSnoozeModalProps {
    typeTitle: string;
    /** The patient's current aligner, when known: an aligner snooze must go past it. */
    currentAligner: number | null;
    /** The patient's aligner count, when known; shown next to the current one and caps a snooze. */
    totalAligners: number | null;
    /** UTC day the Treatment phase began; without it a week snooze can only count from now. */
    treatmentStartDate: string | null;
    /** Whether the patient is linked to Dental Monitoring; without it No-gos is disabled. */
    dmLinked: boolean;
    /** The VA's own threshold when it is a no-go VA, prefilled; null otherwise. */
    currentNogos: number | null;
    submitting: boolean;
    /** The API's refusal, shown under the field. */
    apiError: string | null;
    onSubmit: (trigger: VirtualAppointmentSnoozeTrigger) => void;
    onClose: () => void;
}

// Snoozing sends the VA back to planned with a new trigger; it fires again when that is reached.
export function VirtualAppointmentSnoozeModal({
    typeTitle,
    currentAligner,
    totalAligners,
    treatmentStartDate,
    dmLinked,
    currentNogos,
    submitting,
    apiError,
    onSubmit,
    onClose
}: VirtualAppointmentSnoozeModalProps) {
    const [draft, setDraft] = useState<SnoozeDraft>(() =>
        initialSnoozeDraft(currentNogos, dmLinked)
    );
    const [showErrors, setShowErrors] = useState(false);
    const limits: SnoozeLimits = {
        currentAligner,
        totalAligners,
        treatmentStartDate,
        todayIso: todayUtcIso(),
        dmLinked
    };
    const validation = validateSnoozeDraft(draft, limits);
    const fieldError = showErrors && !validation.ok ? validation.error : undefined;
    const baseline = snoozeTriggerBaseline({
        triggerKind: draft.triggerKind,
        weeks: parseWeeksInput(draft.triggerWeeks),
        weeksFrom: draft.weeksFrom,
        treatmentStartDate,
        todayIso: limits.todayIso,
        aligners: { current: currentAligner, total: totalAligners }
    });
    const patch = (next: Partial<SnoozeDraft>) => setDraft((prev) => ({ ...prev, ...next }));

    const handleSubmit = () => {
        setShowErrors(true);
        if (validation.ok && !submitting) onSubmit(validation.trigger);
    };

    return (
        <ModalShell
            widthClassName="max-w-[460px]"
            dismissible={!submitting}
            onDismiss={onClose}
            labelledBy={TITLE_ID}
        >
            <div className="flex justify-between items-center mb-4">
                <h2 id={TITLE_ID} className="text-xl font-bold text-gray-900 truncate">
                    Snooze {typeTitle}
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    aria-label="Close"
                    className="text-gray-400 hover:text-gray-800 disabled:opacity-50"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
                The VA goes back to planned and opens again at the new trigger.
            </p>
            <VirtualAppointmentTriggerField
                inputId={TRIGGER_INPUT_ID}
                triggerKind={draft.triggerKind}
                triggerAligner={draft.triggerAligner}
                triggerWeeks={draft.triggerWeeks}
                triggerNogos={draft.triggerNogos}
                error={fieldError}
                weeksFrom={{
                    value: draft.weeksFrom,
                    treatmentStartUnavailableHint:
                        treatmentStartDate === null ? NO_TREATMENT_START_ON_RECORD : undefined,
                    onChange: (weeksFrom) => patch({ weeksFrom })
                }}
                nogosUnavailableHint={nogosUnavailableHint(dmLinked)}
                baseline={baseline}
                onKindChange={(triggerKind: PlannableTriggerKind) => patch({ triggerKind })}
                onAlignerChange={(triggerAligner) => patch({ triggerAligner })}
                onWeeksChange={(triggerWeeks) => patch({ triggerWeeks })}
                onNogosChange={(triggerNogos) => patch({ triggerNogos })}
            />
            {apiError && (
                <p role="alert" className="mt-3 text-sm text-red-500">
                    {apiError}
                </p>
            )}
            <div className="flex justify-end items-center gap-3 mt-6">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || (showErrors && !validation.ok)}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {submitting ? 'Snoozing…' : 'Snooze'}
                </button>
            </div>
        </ModalShell>
    );
}
